import React from "react"
import clsx from "clsx"
import { useTranslation } from "react-i18next"
import { Dialog, Transition } from "@headlessui/react"

import { CrossIcon } from "components/Elements/Icons"
import "./ModalDialog.scoped.scss"

// export type ModalProps = {
// 	title: string | React.ReactNode
// 	isOpen: boolean
// 	closeModal: () => void
// 	className?: string
// 	classNameBody?: string
// 	children?: React.ReactNode
// 	onClose?: () => void
// 	footer?: React.ReactNode
// } & FormIconProps

export const ModalDialog = React.forwardRef(
	(
		{
			title,
			isOpen,
			closeModal,
			className = "",
			classNameBody = "c-modal-dialog__body",
			children,
			onClose,
			startIcon,
			footer,
		},
		ref
	) => {
		const { t } = useTranslation()
		const modalPreviousState = React.useRef(false)
		const modalBodyRef = React.useRef(null)

		React.useEffect(() => {
			// Trigger onClose callback each time the modal is closed
			// in whatever way
			const wasOpen = modalPreviousState.current
			const modalIsClosing = wasOpen && !isOpen
			if (modalIsClosing && onClose) onClose()
			modalPreviousState.current = isOpen
		}, [isOpen, onClose])

		// Extend the component instance
		React.useImperativeHandle(ref, () => ({
			scrollBodyToTop() {
				modalBodyRef?.current?.scrollTo(0, 0)
			},
		}))

		return (
			<Transition appear show={isOpen} as={React.Fragment}>
				<Dialog
					open={isOpen}
					onClose={() => closeModal()}
					as="div"
					className="c-modal-dialog inset-0"
				>
					<div className="flex items-center justify-center min-h-screen px-4 text-center">
						<Dialog.Overlay className="c-modal-dialog__overlay inset-0" />

						{/* This element is to trick the browser into centering the modal contents. */}
						{/* <span className="inline-block h-screen align-middle" aria-hidden="true">
							&#8203;
						</span> */}
						<Transition.Child
							as={React.Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<div
								className={clsx(
									"c-modal-dialog__container transition-all transform shadow-xl divide-y",
									className
								)}
							>
								{/* -- Header / Title -------- */}
								<header className="c-modal-dialog__header w-full flex px-4 md:px-6 py-6 items-center">
									{startIcon && <div className="mr-3">{startIcon}</div>}
									{title && 
										<Dialog.Title as="h2" className="c-h2 w-full text-primary font-bold text-center mt-7">
											 {title}
										</Dialog.Title>
									}
									<button
										onClick={() => closeModal()}
										aria-label={t("modal.close", { ns: "common" })}
										type="button"
										className="c-modal-dialog__close p-2"
									>
										<CrossIcon size={25} />
										<span className="mt-1 text-xxs">
											{t("global.close")}
										</span>
									</button>
								</header>

								{/* -- Body -------- */}
								<div className={classNameBody} ref={modalBodyRef}>
									{children}
								</div>

								{/* -- Footer -------- */}
								{footer && <footer className="flex flex-col p-6">{footer}</footer>}
							</div>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition>
		)
	}
)
